import { BDSColor } from '../tokens/color';
import { v2Color } from '../tokens/v2/cssVars';
import { hexToRgba } from '../utils';

/**
 * v2 디자인시스템 토큰 매핑 적용 — 레거시 시맨틱 리프를 v2 토큰(`v2Color.*` = `var(--v2-*)`)에 alias.
 * 브랜드·모드는 앱 루트 `V2BrandVarsGlobal`이 `:root`에 주입하는 `--v2-*`가 결정하므로,
 * 리프의 bubblechat/tingle × light/dark 4값은 동일 참조로 둔다.
 * (매핑 근거: Figma "구 시스템 → Tingle DS" 색상 매핑 node 546-3.)
 */
const v2 = (ref: string) => ({ light: ref, dark: ref });

export const chatServiceColor = {
  static: {
    white: {
      role: '',
      bubblechat: v2(v2Color.static.white),
      tingle: v2(v2Color.static.white),
    },
    black: {
      role: '',
      bubblechat: v2(v2Color.static.black),
      tingle: v2(v2Color.static.black),
    },
    primary: {
      role: '',
      bubblechat: v2(v2Color.brand.primary),
      tingle: v2(v2Color.brand.primary),
    },
    secondary: {
      role: '',
      bubblechat: v2(v2Color.brand.secondary),
      tingle: v2(v2Color.brand.secondary),
    },
    warning: {
      role: '버블챗/팅글 경고문 색상',
      bubblechat: v2(v2Color.system.red),
      tingle: v2(v2Color.system.red),
    },
    adult: {
      // Figma legacy/nsfw(bubblechat #F6375A · tingle #FF4444) 유지 — 브랜드별로 값이 갈리므로 v2() alias 불가.
      // v2 system/nsfw로의 전환은 디자인 확정 후 별도로 진행한다.
      role: '성인/스릴 모드 표시 색상',
      bubblechat: {
        light: BDSColor.red[50],
        dark: BDSColor.red[50],
      },
      tingle: {
        light: BDSColor.red[55],
        dark: BDSColor.red[55],
      },
    },
    notice: {
      // 공지방 표식(BubblePickIcon) 색. 앱과 같은 값을 써야 해서 v2() alias 대상이 아니다 —
      // 앱 실측 #6A7FEF(스크린샷 다운스케일로 배경과 블렌딩된 값) ≈ moody_blue[50] #748AF8.
      // v2 팔레트에 대응 토큰이 없어 구 램프를 유지한다(static/adult와 같은 예외).
      // 버블챗은 리브랜딩 대상이 아니라 구 뱃지색(aquamarine[50] #00E0FF = 구 static/primary)을 유지한다.
      role: '공지방 표식 색상',
      bubblechat: {
        light: BDSColor.aquamarine[50],
        dark: BDSColor.aquamarine[50],
      },
      tingle: {
        light: BDSColor.moody_blue[50],
        dark: BDSColor.moody_blue[50],
      },
    },
  },
  field: {
    background: {
      // 페이지 최하단 배경 = --v2-page-bg(cssVars.ts). 팅글은 elevated, 버블챗은 브라우저 primary ·
      // 인앱 secondary 로 갈리는데 그 분기를 여기서 복제하지 않고 파생 변수 하나를 가리킨다.
      // (팅글을 primary(#000)로 두면 페이지보다 어두운 순검정이 되어 페이지 위에 얹힌 면이
      //  전부 검게 뜬다. 카드는 이 토큰이 아니라 field.side 를 쓴다.)
      role: '최하단에 깔리는 bg 색상',
      bubblechat: v2(v2Color.bg.page),
      tingle: v2(v2Color.bg.page),
    },
    dimmer: {
      role: '썸네일 내부 랭크 색상',
      bubblechat: v2(v2Color.bg.dim),
      tingle: v2(v2Color.bg.dim),
    },
    content: {
      role: '콘텐츠 필드 색상',
      bubblechat: v2(v2Color.bg.secondary),
      tingle: v2(v2Color.bg.secondary),
    },
    side: {
      // 페이지 위에 얹히는 패널(내정보 코인바 802:73716 등)이 쓰는 면. Figma가 참조하는 구 토큰은
      // Field/Side(다크 #201E1E)로, content(=bg/secondary)보다 한 단 위다. 팅글은 페이지 배경이
      // elevated(#191C19)라 secondary(#181A18)를 쓰면 패널이 배경에 묻혀 경계가 사라진다.
      // 매핑표(546:77)는 background/muted를 '근사'로 적었지만 팅글 muted는 #3B3F3B로 너무 밝아,
      // 원본 #201E1E에 가장 가까운 background/tertiary(#252825)로 정렬한다.
      // 버블챗 브라우저는 리브랜딩 대상이 아니라 기존 구현값(content와 동일)을 유지하고, 인앱은 페이지가
      // secondary 로 올라가므로 tertiary 로 한 단 따라 올라간다 — 분기는 --v2-panel-bg(cssVars.ts) 한 곳.
      role: '페이지 위 패널 색상',
      bubblechat: v2(v2Color.bg.panel),
      tingle: v2(v2Color.bg.panel),
    },
    input: {
      role: '인풋 필드 색상',
      bubblechat: v2(v2Color.bg.secondary),
      tingle: v2(v2Color.bg.secondary),
    },
    toast: {
      role: '',
      bubblechat: {
        light: BDSColor.cool[35],
        dark: BDSColor.cool[55],
      },
      tingle: {
        light: BDSColor.warm[35],
        dark: BDSColor.warm[55],
      },
    },
    scroll: {
      role: '',
      bubblechat: {
        light: hexToRgba(BDSColor.common.white, 0.2),
        dark: hexToRgba(BDSColor.cool[75], 0.2),
      },
      tingle: {
        light: hexToRgba(BDSColor.common.white, 0.2),
        dark: hexToRgba(BDSColor.warm[75], 0.2),
      },
    },
    areaChart: {
      // 이름은 차트지만 실제로는 선택된 항목의 강조 배경(대화방 설정·출력량 조절·메시지카드 선택 등)으로 쓰인다.
      // 구 값은 브랜드색 8% 틴트(버블챗 아쿠아마린·팅글 핑크)라 brand/primary 틴트로 통일한다(디자인 확정, 2026-08-12).
      role: '면적 차트 배경 색상',
      bubblechat: v2(v2Color.highlight.primary['10']),
      tingle: v2(v2Color.highlight.primary['10']),
    },
  },
  buttons: {
    disable: {
      // Figma Box Button States=Inactive(460:17490)가 background/muted를 쓴다 → bg.muted로 정렬.
      // (546-3표의 background/tertiary는 너무 연해 흰 배경과 안 구분되던 문제도 muted가 해소)
      // 짝이 되는 글자색은 label/primary — 소비처 &:disabled에서 지정한다.
      role: '기본 버튼 사용 불가',
      bubblechat: v2(v2Color.bg.muted),
      tingle: v2(v2Color.bg.muted),
    },
    default: {
      // 매핑표 Buttons/Line/Disable #E7E7E7 → border/default.
      role: '기본 버튼 색상',
      bubblechat: v2(v2Color.border.default),
      tingle: v2(v2Color.border.default),
    },
    menu_active: {
      // 팅글은 구 값(warm[60] #3B3939)이 raw 램프라 v2 리스킨이 전파되지 않아 초록 계열 뉴트럴
      // 사이에서 겉돌았다 → 패널·선택 메뉴가 쓰는 '한 단 올라온 면' = background/muted로 정렬.
      // 버블챗은 리브랜딩 대상이 아니므로 구 램프를 그대로 유지한다.
      role: '메뉴 버튼',
      bubblechat: {
        light: BDSColor.cool[10],
        dark: BDSColor.cool[60],
      },
      tingle: v2(v2Color.bg.muted),
    },
    menu_hover: {
      role: '메뉴 버튼 마우스 호버 시',
      bubblechat: {
        light: BDSColor.cool[5],
        dark: BDSColor.cool[55],
      },
      tingle: {
        light: BDSColor.warm[55],
        dark: BDSColor.warm[55],
      },
    },
    hover: {
      role: '기본 버튼 마우스 호버 시',
      bubblechat: {
        light: BDSColor.neutral[10],
        dark: BDSColor.neutral[60],
      },
      tingle: {
        light: BDSColor.neutral[10],
        dark: BDSColor.neutral[60],
      },
    },
    press: {
      role: '기본 버튼 클릭 시',
      bubblechat: {
        light: BDSColor.variant[30],
        dark: BDSColor.neutral[65],
      },
      tingle: {
        light: BDSColor.variant[30],
        dark: BDSColor.neutral[65],
      },
    },
    sbtn_default: {
      role: '서브 버튼 기본 색상',
      bubblechat: v2(v2Color.button.subBg),
      tingle: v2(v2Color.button.subBg),
    },
    sbtn_hover: {
      role: '서브 버튼 마우스 호버 시',
      bubblechat: {
        light: BDSColor.variant[35],
        dark: BDSColor.neutral[55],
      },
      tingle: {
        light: BDSColor.variant[35],
        dark: BDSColor.neutral[55],
      },
    },
    sbtn_press: {
      role: '서브 버튼 클릭 시',
      bubblechat: {
        light: BDSColor.variant[50],
        dark: BDSColor.neutral[65],
      },
      tingle: {
        light: BDSColor.variant[50],
        dark: BDSColor.neutral[65],
      },
    },
    thbtn_default: {
      role: '3순위 버튼 색상',
      bubblechat: {
        light: BDSColor.variant[55],
        dark: BDSColor.neutral[65],
      },
      tingle: {
        light: BDSColor.variant[55],
        dark: BDSColor.neutral[65],
      },
    },

    btn_highlight: {
      role: '충전소 내 강조 버튼, 텍스트 색상',
      bubblechat: {
        light: BDSColor.pink[50],
        dark: BDSColor.pink[50],
      },
      tingle: {
        light: BDSColor.moody_blue[50],
        dark: BDSColor.moody_blue[50],
      },
    },
    btn_hyper: {
      role: '하이퍼 샴푸 구매 버튼 색상',
      bubblechat: {
        light: BDSColor.cobalt_blue[25],
        dark: BDSColor.cobalt_blue[25],
      },
      tingle: {
        light: BDSColor.purple[20],
        dark: BDSColor.purple[20],
      },
    },
  },
  sideMenu: {
    menu_hover: {
      role: '사이드 메뉴 호버 시',
      bubblechat: {
        light: BDSColor.cool[5],
        dark: BDSColor.cool[55],
      },
      tingle: {
        light: BDSColor.warm[5],
        dark: BDSColor.warm[55],
      },
    },
    menu_active: {
      role: '사이드 메뉴 클릭 시',
      bubblechat: {
        light: BDSColor.cool[10],
        dark: BDSColor.cool[60],
      },
      tingle: {
        light: BDSColor.warm[10],
        dark: BDSColor.warm[60],
      },
    },
    new: {
      role: '만들기 버튼 기본 색상',
      bubblechat: {
        light: BDSColor.skyblue_default[25],
        // dark: BDSColor.skyblue_dark[25],
        dark: BDSColor.skyblue_dark[50],
      },
      tingle: {
        light: BDSColor.pink_dark[25],
        dark: BDSColor.pink_dark[25],
      },
    },
    new_hover: {
      role: '만들기 버튼 마우스 호버 시',
      bubblechat: {
        light: BDSColor.skyblue_default[50],
        // dark: BDSColor.skyblue_dark[50],
        dark: BDSColor.skyblue_dark[25],
      },
      tingle: {
        light: BDSColor.pink[55],
        dark: BDSColor.pink_dark[50],
      },
    },
  },
  item: {
    review: {
      role: 'Text',
      sub_description: 'Text',
      bubblechat: {
        light: BDSColor.skyblue_default[5],
        dark: BDSColor.skyblue_default[5],
      },
      tingle: {
        light: BDSColor.pink_dark[5],
        dark: BDSColor.pink_dark[5],
      },
    },
    list_hover: {
      role: '리스트 아이템 마우스 오버 시',
      bubblechat: {
        light: BDSColor.neutral[10],
        dark: hexToRgba(BDSColor.neutral[10], 0.08),
      },
      tingle: {
        light: BDSColor.neutral[10],
        dark: hexToRgba(BDSColor.neutral[10], 0.08),
      },
    },
    tag: {
      role: '태그 bg 색상',
      sub_description: 'Text',
      bubblechat: v2(v2Color.chat.bubbleIn),
      tingle: v2(v2Color.chat.bubbleIn),
    },
    tag_edit: {
      role: '편집 가능한 태그 bg 색상',
      sub_description: 'Text',
      bubblechat: {
        light: BDSColor.variant[30],
        dark: BDSColor.cool[55],
      },
      tingle: {
        light: BDSColor.tingle_variant[30],
        dark: BDSColor.warm[55],
      },
    },
    using_tag: {
      role: '사용 중 태그 bg 색상',
      sub_description: 'Text',
      bubblechat: {
        light: hexToRgba('#63C6FC', 0.2),
        dark: hexToRgba('#63C6FC', 0.2),
      },
      tingle: {
        light: hexToRgba('#63C6FC', 0.2),
        dark: hexToRgba('#63C6FC', 0.2),
      },
    },
    highlight: {
      role: '충전소 내 강조 버튼, 텍스트 색상',
      // v2 매핑: 두 서비스 모두 brand/primary. 팅글은 구 브랜드색(핑크)을 살리려 brand/secondary에 붙였었는데,
      // 리브랜딩 후 강조는 brand/primary(노랑)로 통일한다(디자인 확정, 2026-08-12).
      bubblechat: v2(v2Color.brand.primary),
      tingle: v2(v2Color.brand.primary),
    },
    // item/label
    lt_green: {
      role: '라벨 Text 색상(LLM 모델 라벨)',
      bubblechat: v2(v2Color.system.green),
      tingle: v2(v2Color.system.green),
    },
    lt_aquamarine: {
      // 브랜드 강조 텍스트(고객센터 배지·LLM 모델 라벨). 구 값은 버블챗 aquamarine[50]·팅글 primary_pink[50](#FF5776)로
      // 리브랜딩 전 브랜드색이라 v2로 옮긴다. 두 서비스 모두 brand/primary로 통일(디자인 확정, 2026-08-12).
      role: '라벨 Text 색상(LLM 모델 라벨)',
      bubblechat: v2(v2Color.brand.primary),
      tingle: v2(v2Color.brand.primary),
    },
    lt_blue: {
      role: '라벨 Text 색상(LLM 모델 라벨)',
      bubblechat: v2(v2Color.system.blue),
      tingle: v2(v2Color.system.blue),
    },
    lt_purple: {
      role: '라벨 Text 색상(LLM 모델 라벨)',
      bubblechat: {
        light: BDSColor.purple[20],
        dark: BDSColor.purple[20],
      },
      tingle: {
        light: BDSColor.purple[20],
        dark: BDSColor.purple[20],
      },
    },
    lt_magenta: {
      role: '라벨 Text 색상(LLM 모델 라벨)',
      bubblechat: {
        light: BDSColor.magenta[40],
        dark: BDSColor.magenta[40],
      },
      tingle: {
        light: BDSColor.magenta[40],
        dark: BDSColor.magenta[40],
      },
    },
    lt_red: {
      role: '라벨 Text 색상(LLM 모델 라벨)',
      bubblechat: {
        light: BDSColor.primary_pink[50],
        dark: BDSColor.primary_pink[50],
      },
      tingle: {
        light: BDSColor.aquamarine[50],
        dark: BDSColor.aquamarine[50],
      },
    },
    lt_black: {
      // 매핑표 Semantic/Black #000000 → static/black. 다만 이 토큰은 light #000000 / dark #FFFFFF로 모드 반전이라 label/primary가 정확히 일치한다.
      role: '라벨 Text 색상(LLM 모델 라벨)',
      bubblechat: v2(v2Color.label.primary),
      tingle: v2(v2Color.label.primary),
    },
    lb_green: {
      role: '라벨 bg 색상(LLM 모델 라벨)',
      bubblechat: {
        light: hexToRgba(BDSColor.green[50], 0.1),
        dark: hexToRgba(BDSColor.green[50], 0.1),
      },
      tingle: {
        light: hexToRgba(BDSColor.green[50], 0.1),
        dark: hexToRgba(BDSColor.green[50], 0.1),
      },
    },
    lb_aquamarine: {
      // lt_aquamarine과 짝을 이루는 틴트 배경. CSS 변수라 hexToRgba를 쓸 수 없어 전용 틴트 토큰(highlight/*)을 쓴다.
      role: '라벨 bg 색상(LLM 모델 라벨)',
      bubblechat: v2(v2Color.highlight.primary['10']),
      tingle: v2(v2Color.highlight.primary['10']),
    },
    lb_aquamarine2: {
      // 구 값은 브랜드색 최연한 톤(버블챗 aquamarine[0]·팅글 primary_pink[5]). 짝이 되는 글자색이 item/highlight라
      // 같은 brand/primary 틴트로 통일한다(디자인 확정, 2026-08-12).
      role: '라벨 bg 색상(멀티 이미지 대표 라벨)',
      bubblechat: v2(v2Color.highlight.primary['10']),
      tingle: v2(v2Color.highlight.primary['10']),
    },
    lb_blue: {
      role: '라벨 bg 색상(LLM 모델 라벨)',
      // 짝이 되는 글자색이 lt_blue(system/blue)라 브랜드 틴트가 아닌 파란 틴트(highlight/info)를 쓴다.
      // 이전에는 브랜드 틴트라 팅글에서 핑크 배경 + 파란 글자로 어긋났다.
      bubblechat: v2(v2Color.highlight.info['10']),
      tingle: v2(v2Color.highlight.info['10']),
    },
    lb_purple: {
      role: '라벨 bg 색상(LLM 모델 라벨)',
      bubblechat: {
        light: hexToRgba(BDSColor.purple[20], 0.1),
        dark: hexToRgba(BDSColor.purple[20], 0.1),
      },
      tingle: {
        light: hexToRgba(BDSColor.purple[20], 0.1),
        dark: hexToRgba(BDSColor.purple[20], 0.1),
      },
    },
    lb_magenta: {
      role: '라벨 bg 색상(LLM 모델 라벨)',
      bubblechat: {
        light: hexToRgba(BDSColor.magenta[40], 0.1),
        dark: hexToRgba(BDSColor.magenta[40], 0.1),
      },
      tingle: {
        light: hexToRgba(BDSColor.magenta[40], 0.1),
        dark: hexToRgba(BDSColor.magenta[40], 0.1),
      },
    },
    lb_red: {
      role: '라벨 bg 색상(LLM 모델 라벨)',
      bubblechat: {
        light: hexToRgba(BDSColor.primary_pink[50], 0.08),
        dark: hexToRgba(BDSColor.primary_pink[50], 0.08),
      },
      tingle: {
        light: hexToRgba(BDSColor.aquamarine[50], 0.08),
        dark: hexToRgba(BDSColor.aquamarine[50], 0.08),
      },
    },
    lb_black: {
      role: '라벨 bg 색상(LLM 모델 라벨)',
      bubblechat: {
        light: hexToRgba(BDSColor.common.black, 0.05),
        dark: hexToRgba(BDSColor.common.white, 0.1),
      },
      tingle: {
        light: hexToRgba(BDSColor.common.black, 0.05),
        dark: hexToRgba(BDSColor.common.white, 0.1),
      },
    },
    lb_gray: {
      role: '멀티이미지 썸네일 색상',
      bubblechat: {
        light: BDSColor.neutral[10],
        dark: BDSColor.neutral[10],
      },
      tingle: {
        light: BDSColor.neutral[10],
        dark: BDSColor.neutral[10],
      },
    },
    lb_warning: {
      role: '경고 배경 색상',
      bubblechat: {
        light: hexToRgba('#FE008A', 0.1),
        dark: hexToRgba('#FE008A', 0.1),
      },
      tingle: {
        light: hexToRgba('#FF4444', 0.1),
        dark: hexToRgba('#FF4444', 0.1),
      },
    },
    lb_gradient1: {
      role: '후원 라벨 색상1',
      bubblechat: {
        light: BDSColor.purple[55],
        dark: BDSColor.purple[55],
      },
      tingle: {
        light: BDSColor.magenta[45],
        dark: BDSColor.magenta[45],
      },
    },
    lb_gradient2: {
      role: '후원 라벨 색상2',
      bubblechat: {
        light: BDSColor.magenta[60],
        dark: BDSColor.magenta[60],
      },
      tingle: {
        light: BDSColor.purple[25],
        dark: BDSColor.purple[25],
      },
    },
    // item/Station
    stationGreen: {
      role: '라벨 배경 색상(무료 충전소 버튼)',
      bubblechat: {
        light: BDSColor.green[50],
        dark: BDSColor.green[50],
      },
      tingle: {
        light: BDSColor.green[50],
        dark: BDSColor.green[50],
      },
    },
    stationYellow: {
      role: '라벨 배경 색상(무료 충전소 버튼)',
      bubblechat: v2(v2Color.currency.coin),
      tingle: v2(v2Color.currency.coin),
    },
    stationPurple: {
      role: '라벨 배경 색상(무료 충전소 버튼)',
      bubblechat: {
        light: BDSColor.purple[50],
        dark: BDSColor.purple[50],
      },
      tingle: {
        light: BDSColor.purple[50],
        dark: BDSColor.purple[50],
      },
    },
    stationBlue: {
      role: '라벨 배경 색상(무료 충전소 버튼)',
      bubblechat: {
        light: BDSColor.aquamarine[50],
        dark: BDSColor.aquamarine[50],
      },
      tingle: {
        light: BDSColor.aquamarine[50],
        dark: BDSColor.aquamarine[50],
      },
    },
    stationPink: {
      role: '라벨 배경 색상(무료 충전소 버튼)',
      bubblechat: {
        light: BDSColor.magenta[50],
        dark: BDSColor.magenta[50],
      },
      tingle: {
        light: BDSColor.magenta[50],
        dark: BDSColor.magenta[50],
      },
    },
    writer: {
      role: 'writer 버튼 색상',
      bubblechat: {
        light: hexToRgba(BDSColor.cool[0], 0.24),
        dark: hexToRgba(BDSColor.cool[0], 0.24),
      },
      tingle: {
        light: hexToRgba(BDSColor.warm[0], 0.24),
        dark: hexToRgba(BDSColor.warm[0], 0.24),
      },
    },
  },
  message: {
    textbox: {
      role: '슬라이드바 배경 색상',
      bubblechat: {
        light: hexToRgba(BDSColor.common.white, 0.8),
        dark: hexToRgba(BDSColor.common.black, 0.75),
      },
      tingle: {
        light: hexToRgba(BDSColor.common.white, 0.8),
        dark: hexToRgba(BDSColor.common.black, 0.75),
      },
    },
    slideBar: {
      role: '대화방 텍스트 영역',
      bubblechat: {
        light: hexToRgba(BDSColor.common.white, 0.9),
        dark: hexToRgba(BDSColor.common.black, 0.9),
      },
      tingle: {
        light: hexToRgba(BDSColor.common.white, 0.9),
        dark: hexToRgba(BDSColor.common.black, 0.9),
      },
    },
    talk_mode: {
      character: {
        role: '캐릭터의 말풍선 bg 색상',
        bubblechat: {
          light: BDSColor.cool[10],
          dark: BDSColor.cool[55],
        },
        tingle: {
          light: BDSColor.warm[55],
          dark: BDSColor.warm[55],
        },
      },
      user: {
        role: '사용자의 말풍선 bg 색상',
        bubblechat: {
          light: BDSColor.cool[10],
          dark: BDSColor.cool[60],
        },
        tingle: {
          light: BDSColor.warm[60],
          dark: BDSColor.warm[60],
        },
      },
      action: {
        role: '지문 bg 색상',
        bubblechat: {
          light: BDSColor.variant[5],
          dark: BDSColor.cool[70],
        },
        tingle: {
          light: BDSColor.tingle_variant[5],
          dark: BDSColor.warm[70],
        },
      },
    },
    novel_mode: {
      character: {
        role: '캐릭터 TEXT 색상',
        bubblechat: {
          light: BDSColor.neutral[70],
          dark: BDSColor.neutral[8],
        },
        tingle: {
          light: BDSColor.neutral[70],
          dark: BDSColor.neutral[8],
        },
      },
      user: {
        role: '사용자 TEXT 색상',
        bubblechat: {
          light: '#3900d7',
          dark: '#c7b2ff',
        },
        tingle: {
          light: '#3900d7',
          dark: '#ffbffc',
        },
      },
      narrative: {
        role: '지문 색상',
        bubblechat: {
          light: '#464646',
          dark: '#c1c1c1',
        },
        tingle: {
          light: '#464646',
          dark: '#c1c1c1',
        },
      },
    },
  },
  popup: {
    background: {
      role: '팝업 bg 색상',
      bubblechat: v2(v2Color.bg.elevated),
      tingle: v2(v2Color.bg.elevated),
    },
    background_dark: {
      role: '신규 유저 플로우 팝업 bg 색상',
      bubblechat: {
        light: BDSColor.cool[65],
        dark: BDSColor.cool[65],
      },
      tingle: {
        light: BDSColor.warm[65],
        dark: BDSColor.warm[65],
      },
    },
    content_dark: {
      role: '신규 유저 플로우 팝업 내의 콘텐츠 필드 색상',
      bubblechat: {
        light: BDSColor.cool[70],
        dark: BDSColor.cool[70],
      },
      tingle: {
        light: BDSColor.warm[70],
        dark: BDSColor.warm[70],
      },
    },
    input: {
      role: '팝업 내의 인풋 bg 색상',
      bubblechat: {
        light: BDSColor.neutral[15],
        dark: BDSColor.cool[70],
      },
      tingle: {
        light: BDSColor.neutral[15],
        dark: BDSColor.warm[70],
      },
    },
  },
  icon: {
    icon_1: {
      role: '타이틀과 동일 라인에 사용하는 색상, 타이틀보다 중요도 낮을 때 사용',
      bubblechat: {
        light: BDSColor.neutral[30],
        dark: BDSColor.neutral[48],
      },
      tingle: {
        light: BDSColor.neutral[30],
        dark: BDSColor.neutral[48],
      },
    },
    icon_2: {
      role: '',
      bubblechat: {
        light: BDSColor.neutral[38],
        dark: BDSColor.neutral[38],
      },
      tingle: {
        light: BDSColor.neutral[38],
        dark: BDSColor.neutral[38],
      },
    },
    icon_3: {
      role: '',
      bubblechat: {
        light: BDSColor.neutral[55],
        dark: BDSColor.neutral[20],
      },
      tingle: {
        light: BDSColor.neutral[55],
        dark: BDSColor.neutral[20],
      },
    },
    icon_4: {
      // 시안 매핑상 Buttons/icon/Active → text/accent 이나, icon_4는 헤더 검색·알림 등 범용 아이콘
      // 색이라 accent(브랜드색)로 두면 전 아이콘이 물든다. 일반 아이콘색 label.primary로 매핑.
      role: '주요 기능 아이콘 색상',
      bubblechat: v2(v2Color.label.primary),
      tingle: v2(v2Color.label.primary),
    },
  },
  line: {
    line_1: {
      // 앱 전반의 기본 구분선. 리브랜딩 초기 매핑은 border/subtle(8%)이었는데,
      // 구 값이 #323434(불투명)이라 전 화면의 선이 한 단계 흐려졌다(헤더·사이드바·검색·마이 등).
      // 다크 기준 border/default(20%)가 #323434와 사실상 같은 밝기라 그쪽으로 맞춘다.
      role: '',
      bubblechat: v2(v2Color.border.default),
      tingle: v2(v2Color.border.default),
    },
    line_2: {
      // 매핑표(546:133) Line/2 · line-02 #D8D8D8 → border/default. separator로 잘못 걸려 있었다.
      // separator는 매핑표상 Line/1(앱)의 대상이고, 팅글에선 값이 bg/muted와 같아(#3B3F3B)
      // muted 면 위에 놓인 컨트롤 테두리가 배경과 완전히 같은 색이 돼 사라졌다.
      // 정정은 리브랜딩 대상인 팅글에만 적용한다 — 버블챗은 기존 값(separator)을 그대로 둔다.
      role: '',
      bubblechat: v2(v2Color.separator),
      tingle: v2(v2Color.border.default),
    },
    line_3: {
      role: '',
      bubblechat: v2(v2Color.border.default),
      tingle: v2(v2Color.border.default),
    },
    line_6: {
      // 매핑표 Line/2 · line-02 #D8D8D8 → border/default (line_3와 동일 매핑).
      role: '프로필 라인 색상',
      sub_description: '유저 프로필',
      bubblechat: v2(v2Color.border.default),
      tingle: v2(v2Color.border.default),
    },
  },
  text: {
    default: {
      role: '기본 텍스트 색상',
      bubblechat: v2(v2Color.label.primary),
      tingle: v2(v2Color.label.primary),
    },
    on_accent: {
      role: 'accent(브랜드 강조) 배경 위 텍스트 색상',
      sub_description: '브랜드 primary 버튼 등 강조 배경 위 글자',
      bubblechat: v2(v2Color.label.onAccent),
      tingle: v2(v2Color.label.onAccent),
    },
    default_2: {
      role: '기본 텍스트 색상 2',
      bubblechat: {
        light: BDSColor.common.white,
        dark: BDSColor.common.black,
      },
      tingle: {
        light: BDSColor.common.white,
        dark: BDSColor.common.black,
      },
    },
    TXT_0: {
      role: '중요도가 낮은 텍스트 색상',
      // 매핑표(node 546-3): Text/0 #BCBCBC → label/tertiary. (표의 '비활성=opacity/30'은
      // 비활성 상태 규칙이지 기본값이 quaternary라는 뜻이 아니다 — 기본값을 30%로 두면
      // 플레이스홀더가 한 단 더 흐려진다.)
      sub_description: 'Input Field의 가이드 텍스트 컬러',
      bubblechat: v2(v2Color.label.tertiary),
      tingle: v2(v2Color.label.tertiary),
    },
    TXT_1: {
      role: '텍스트가 밀집된 영역에서 중요도가 낮은 텍스트의 컬러',
      sub_description: 'Info text, sub text',
      bubblechat: v2(v2Color.label.tertiary),
      tingle: v2(v2Color.label.tertiary),
    },
    TXT_2: {
      role: '본문 텍스트에 사용되는 색상',
      sub_description: '본문',
      bubblechat: v2(v2Color.label.secondary),
      tingle: v2(v2Color.label.secondary),
    },
    TXT_3: {
      role: '페이지에서 상위 레벨의 중요도를 갖는 텍스트에 사용되는 색상',
      sub_description: '타이틀',
      bubblechat: v2(v2Color.label.primary),
      tingle: v2(v2Color.label.primary),
    },
    info: {
      role: 'Info',
      sub_description: 'AI 모델 선택 모달 > 사용 중, 버블 표시 텍스트',
      bubblechat: v2(v2Color.system.blue),
      tingle: v2(v2Color.system.blue),
    },
  },
  theme: {
    chat: {
      character: {
        role: '대화모드 - 프리셋 버블/팅글 - 캐릭터의 말풍선 bg 색상',
        bubblechat: {
          light: BDSColor.aquamarine[5],
          dark: '#2D3C44',
        },
        tingle: {
          light: '#40212A',
          dark: '#40212A',
        },
      },
      user: {
        role: '대화모드 - 프리셋 버블/팅글 - 사용자의 말풍선 bg 색상',
        bubblechat: {
          light: BDSColor.aquamarine[10],
          dark: '#1A262C',
        },
        tingle: {
          light: '#2B161C',
          dark: '#2B161C',
        },
      },
      textField: {
        role: '대화모드 - 프리셋 버블/팅글 - 대화 영역 bg 색상',
        bubblechat: {
          light: hexToRgba(BDSColor.common.white, 0.8),
          dark: hexToRgba('#070A0C', 0.8),
        },
        tingle: {
          light: hexToRgba('#100609', 0.8),
          dark: hexToRgba('#100609', 0.8),
        },
      },
    },
    novel: {
      character: {
        role: '소설모드 - 프리셋 버블/팅글 - 캐릭터의 text 색상',
        bubblechat: {
          light: BDSColor.neutral[70],
          dark: BDSColor.neutral[8],
        },
        tingle: {
          light: BDSColor.neutral[8],
          dark: BDSColor.neutral[8],
        },
      },
      user: {
        role: '소설모드 - 프리셋 버블/팅글 - 사용자의 text 색상',
        bubblechat: {
          light: BDSColor.aquamarine[80],
          dark: BDSColor.aquamarine[50],
        },
        tingle: {
          light: BDSColor.primary_pink[20],
          dark: BDSColor.primary_pink[20],
        },
      },
    },
  },
  gradient: {
    gradient_1: {
      role: 'gradient_1',
      sub_description: 'gradient_1',
      bubblechat: {
        light: hexToRgba('#FFFFFF', 0),
        dark: hexToRgba('#111114', 0),
      },
      tingle: {
        light: hexToRgba('#111114', 0),
        dark: hexToRgba('#111114', 0),
      },
    },
    gradient_2: {
      role: 'gradient_2',
      sub_description: 'gradient_2',
      bubblechat: {
        light: hexToRgba('#FFFFFF', 0),
        dark: hexToRgba('#000000', 0),
      },
      tingle: {
        light: hexToRgba('#000000', 0),
        dark: hexToRgba('#000000', 0),
      },
    },
    gradient_3: {
      role: 'gradient_3',
      sub_description: 'gradient_3',
      bubblechat: {
        light: hexToRgba('#FFFFFF', 0),
        dark: hexToRgba('#26262B', 0),
      },
      tingle: {
        light: hexToRgba('#2D2B2B', 0),
        dark: hexToRgba('#2D2B2B', 0),
      },
    },
  },
};
